import { queryClient } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";
import { useMutation } from "@tanstack/react-query";

import { type ConfigurationTrackedFile, fetchConfigurationTrackedFiles } from "~/api/configurations";
import { trackedSystemsQueryOptions } from "~/api/system-tree";
import { systemsQueryKeys } from "~/api/useSystems";
import { commitAtCurrentHead } from "~/branching/feature/commit-at-current-head";
import { useInvalidateSystemBranching } from "~/branching/feature/use-invalidate-system-branching";
import type { TrackedFileChoice, TrackedSystemChoice } from "~/branching/model/branch-change";
import { isProtectedBranchProblem } from "~/libraries/model/publishing-error";
import { blankNotebookFile, notebookBaseName } from "~/notebook/core/blank-notebook";
import { useCreateResource } from "~/resource/data-source/use-create-resource";

type CreateNotebookParams = {
  displayName: string;
  systemId: string;
  branchId: string;
  expectedHeadSnapshotId: string;
  configName: string;
  existingTrackedFiles: ConfigurationTrackedFile[];
  // Source configuration the notebook is added to. Its tracked systems are carried into the new
  // config so creating a notebook never drops the system's subsystems.
  sourceConfigId?: string;
};

type CreateNotebookResult = {
  resourceId: string;
  fileId: string;
  fileRevisionId: string;
  configId: string;
  snapshotId: string;
};

export function useCreateNotebook() {
  const createResource = useCreateResource();
  const invalidateSystemBranching = useInvalidateSystemBranching();

  return useMutation<CreateNotebookResult, unknown, CreateNotebookParams>({
    mutationFn: async ({
      displayName,
      systemId,
      branchId,
      expectedHeadSnapshotId,
      configName,
      existingTrackedFiles,
      sourceConfigId,
    }) => {
      const created = await createResource.mutateAsync({
        file: blankNotebookFile(displayName),
        displayName: notebookBaseName(displayName),
        resourceType: V3Models.ResourceTypeDto.File,
      });

      // The notebook tracks latest so later saves reach the branch on its next commit. The commit
      // replaces the configuration, so a retry after someone else's commit rebuilds it from that head.
      const committed = await commitAtCurrentHead(systemId, branchId, expectedHeadSnapshotId, async (head) => {
        const retrying = head.configurationId !== undefined;
        const configId = head.configurationId ?? sourceConfigId;
        const trackedFiles = retrying ? await fetchConfigurationTrackedFiles(configId!) : existingTrackedFiles;
        const trackedSystems = configId ? await trackedSystemsOf(configId, { fresh: retrying }) : [];
        return {
          kind: "configuration",
          message: configName,
          configuration: {
            name: configName,
            trackedFiles: [...trackedFiles.map(toTrackedFileChoice), { fileId: created.fileId, follows: "latest" }],
            trackedSystems,
          },
        };
      });

      return {
        resourceId: created.resourceId,
        fileId: created.fileId,
        fileRevisionId: created.fileRevisionId,
        configId: committed.configurationId,
        snapshotId: committed.snapshotId,
      };
    },
    onSuccess: (_result, { systemId }) => {
      void invalidateSystemBranching(systemId);
      void queryClient.invalidateQueries({ queryKey: systemsQueryKeys.all });
    },
    onError: (error, { systemId }) => {
      if (isProtectedBranchProblem(error)) void invalidateSystemBranching(systemId);
    },
  });
}

function toTrackedFileChoice(tf: ConfigurationTrackedFile): TrackedFileChoice {
  return tf.specifierType === "Locked" && tf.pinnedFileRevisionId
    ? { fileId: tf.fileId, follows: "revision", revisionId: tf.pinnedFileRevisionId }
    : { fileId: tf.fileId, follows: "latest" };
}

async function trackedSystemsOf(configId: string, { fresh }: { fresh: boolean }): Promise<TrackedSystemChoice[]> {
  const options = trackedSystemsQueryOptions(configId);
  const page = fresh
    ? await queryClient.fetchQuery({ ...options, staleTime: 0 })
    : await queryClient.ensureQueryData(options);
  return (page.items ?? []).map((ts) => ({
    systemId: ts.childSystemId,
    ...(ts.specifierType === "Tag" && ts.pinnedConfigId ? { tagId: ts.pinnedConfigId } : {}),
  }));
}
